import {sortBy} from 'lodash';
import {FEATURE_FLAGS} from '../../../constants';
import {hasFeatureFlag} from './featureFlags';

export type StageProfile = 'OPPORTUNITY' | 'PLANNING' | 'RUNNING' | 'HALTED' | 'DONE';
export type StageLike = {id?: string | null; name?: string | null; profile?: StageProfile | string | null} | null | undefined;
type HasStage = {stage?: StageLike} | null | undefined;

export const stageProfileOf = (project: HasStage): StageProfile | null =>
	(project?.stage?.profile as StageProfile | undefined) ?? null;

export const isProjectLocked = (project: HasStage): boolean => ['DONE', 'HALTED'].includes(stageProfileOf(project) ?? '');
export const isProjectDone = (project: HasStage): boolean => stageProfileOf(project) === 'DONE';
export const isProjectHalted = (project: HasStage): boolean => stageProfileOf(project) === 'HALTED';
export const isProjectOpportunity = (project: HasStage): boolean => stageProfileOf(project) === 'OPPORTUNITY';
export const isProjectPlanning = (project: HasStage): boolean => stageProfileOf(project) === 'PLANNING';
export const isProjectPlanningLike = (project: HasStage): boolean =>
	['PLANNING', 'OPPORTUNITY'].includes(stageProfileOf(project) ?? '');
export const isProjectRunning = (project: HasStage): boolean => stageProfileOf(project) === 'RUNNING';
export const isProjectActive = (project: HasStage): boolean => ['PLANNING', 'RUNNING'].includes(stageProfileOf(project) ?? '');

/** Whether no other stage in the list has this stage's profile, so deleting it warns the admin first. */
export const isLastWithProfile = (stage: StageLike, stages: ReadonlyArray<StageLike>): boolean =>
	!!stage?.profile && !stages.some(other => other?.id !== stage.id && other?.profile === stage.profile);

const STAGE_PROFILES: ReadonlyArray<StageProfile> = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

const asStageProfile = (value: string): StageProfile | null => {
	const upper = value.toUpperCase();
	return STAGE_PROFILES.find(profile => profile === upper) ?? null;
};

/**
 * The translated label of a profile, in either case (`RUNNING`, `running`). Also labels a program's stage and a project's
 * old status, which use the same five values. Any other value is returned as given, and null or undefined as ''.
 */
export const profileLabel = (
	profile: string | null | undefined,
	intl: {formatMessage: (d: {id: string}) => string}
): string => {
	const known = profile ? asStageProfile(profile) : null;
	return known ? intl.formatMessage({id: `project_stage_profile.${known.toLowerCase()}`}) : profile ?? '';
};

/**
 * The stage's label: the name as stored. Stage names are admin text, so they are not translated. With custom project
 * stages off, a default-stage name is shown as its translated profile label instead, when a translation exists.
 */
export const stageLabel = (
	stage: StageLike,
	intl: {formatMessage: (d: {id: string}) => string; messages?: Record<string, unknown>}
): string => {
	const name = stage?.name ?? '';
	if (!name || hasFeatureFlag(FEATURE_FLAGS.CUSTOM_PROJECT_STAGES)) {
		return name;
	}
	// Temporary: remove with the custom_project_stages flag clean-up
	const profile = asStageProfile(name);
	const id = profile && `project_stage_profile.${profile.toLowerCase()}`;
	return id && intl.messages && Object.prototype.hasOwnProperty.call(intl.messages, id) ? intl.formatMessage({id}) : name;
};

/** The stages in admin order: ascending sortOrder, stable, stages without a sortOrder last. Returns a new array. */
export const inAdminOrder = <S extends {sortOrder?: number | null}>(stages: ReadonlyArray<S>): S[] =>
	sortBy(stages, stage => stage.sortOrder ?? Infinity);

/** The order stages are tried in when scheduling a project deliberately: running work first, finished work last. */
export const DELIBERATE_SCHEDULING_ORDER: ReadonlyArray<StageProfile> = [
	'RUNNING',
	'PLANNING',
	'OPPORTUNITY',
	'HALTED',
	'DONE',
];

/** The id of the project's stage, or null when it has none. */
export const stageIdOf = (project: {stage?: {id?: string | null} | null} | null | undefined): string | null =>
	project?.stage?.id ?? null;

/**
 * The stages grouped by profile in the order the profiles are given, admin order within each group. Stages whose
 * profile is not listed are dropped.
 */
export const inProfileGroupOrder = <S extends {sortOrder?: number | null; profile?: string | null}>(
	stages: ReadonlyArray<S>,
	profiles: ReadonlyArray<StageProfile>
): S[] => {
	const ordered = inAdminOrder(stages);
	return profiles.flatMap(profile => ordered.filter(stage => stage.profile === profile));
};

type UnknownStage = {id: string; name?: string | null};

/**
 * The stages of the given projects that are not among the known stages, once per stage id, keeping the name from the
 * first occurrence that has one. Projects without a stage are skipped.
 */
export const unknownStagesOf = (
	projects: ReadonlyArray<{stage?: {id?: string | null; name?: string | null} | null} | null | undefined>,
	known: ReadonlyArray<{id?: string | null}>
): UnknownStage[] => {
	const knownIds = new Set(known.map(stage => stage.id));
	const unknown = new Map<string, UnknownStage>();
	projects.forEach(project => {
		const stage = project?.stage;
		if (!stage?.id || knownIds.has(stage.id)) {
			return;
		}
		const seen = unknown.get(stage.id);
		if (!seen) {
			unknown.set(stage.id, {id: stage.id, ...(stage.name ? {name: stage.name} : {})});
		} else if (!seen.name && stage.name) {
			seen.name = stage.name;
		}
	});
	return [...unknown.values()];
};
